// Account Analytics KPI cards — FE-034 / spec §7.0.5.
//
// Four cards mirroring the BE-029 ``client_analytics`` payload but
// labelled with v2 Account vocabulary. Each card is a Link to the
// downstream surface that the dashboard's drill-through contract
// already names.

import { Link } from "react-router-dom";

import {
  buildAccountFilterUrl,
  buildCommitmentFilterUrl,
  type ClientAnalyticsResponse,
  type DashboardDateRange,
} from "@/features/dashboard";


interface AccountKpiCardsProps {
  data: ClientAnalyticsResponse;
  dateRange: DashboardDateRange;
}


interface KpiConfig {
  /** Stable test/CSS slug. */
  key: "total_accounts"
    | "high_risk_accounts"
    | "avg_completion_rate"
    | "accounts_with_overdue";
  label: string;
  helper: string;
  /** Optional click target. ``null`` renders a non-link card. */
  href: string | null;
  format: (data: ClientAnalyticsResponse) => string;
  accent: string;
}


export function AccountKpiCards({
  data, dateRange,
}: AccountKpiCardsProps) {
  const configs: KpiConfig[] = [
    {
      key: "total_accounts",
      label: "Total accounts",
      helper: "Active employer-clients in your brokerage.",
      href: buildAccountFilterUrl({}, dateRange),
      format: (d) => String(d.total_clients),
      accent: "text-text-primary",
    },
    {
      key: "high_risk_accounts",
      label: "High-risk accounts",
      helper:
        `Accounts carrying ${data.high_risk_threshold}+ overdue Commitments.`,
      href: buildAccountFilterUrl(
        { risk_level: "high", has_overdue: true },
        dateRange,
      ),
      format: (d) => String(d.high_risk_clients),
      accent: "text-ember-ink",
    },
    {
      key: "avg_completion_rate",
      label: "Average completion rate",
      helper:
        "Mean of per-account completion rate over the selected window.",
      href: null,
      format: (d) => `${Math.round(d.avg_completion_rate * 100)}%`,
      accent: "text-sage-ink",
    },
    {
      key: "accounts_with_overdue",
      label: "Accounts with overdue Commitments",
      helper: "At least one open Commitment past its due date.",
      href: buildCommitmentFilterUrl({ overdue: true }, dateRange),
      format: (d) => String(d.clients_with_overdue),
      accent: "text-blocked",
    },
  ];

  return (
    <ul
      data-testid="account-kpi-cards"
      role="list"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
    >
      {configs.map((config) => (
        <li key={config.key} className="h-full">
          <KpiCard config={config} data={data} />
        </li>
      ))}
    </ul>
  );
}


function KpiCard({ config, data }: { config: KpiConfig; data: ClientAnalyticsResponse }) {
  const value = config.format(data);
  const body = (
    <>
      <span className="block text-xs font-medium uppercase tracking-wide text-text-tertiary">
        {config.label}
      </span>
      <span
        className={`mt-1 block text-xl font-semibold ${config.accent}`}
        data-testid={`account-kpi-value-${config.key}`}
      >
        {value}
      </span>
      <span className="mt-1 block text-xs text-text-secondary">
        {config.helper}
      </span>
    </>
  );
  const baseClasses =
    "flex h-full flex-col rounded-lg bg-recessed px-4 py-3 transition-fast ease-out";
  if (config.href === null) {
    return (
      <div
        data-testid={`account-kpi-card-${config.key}`}
        aria-label={`${config.label}: ${value}`}
        className={baseClasses}
      >
        {body}
      </div>
    );
  }
  return (
    <Link
      to={config.href}
      data-testid={`account-kpi-card-${config.key}`}
      aria-label={`${config.label}: ${value}`}
      className={`${baseClasses} hover:bg-raised`}
    >
      {body}
    </Link>
  );
}
